/**
 * Базовые helpers для ECharts. Чтобы все графики использовали единую палитру
 * КОММА, не хардкодили цвета в options и имели одинаковый tooltip.
 *
 * Палитра — фиксированные hex светлой темы КОММА (DESIGN_PORT_SPEC §4):
 * нейтрали + primary blue. Смысловые палитры данных (segmentColors / zoneColors /
 * stateColors) живут в `@/theme/tokens` и НЕ перекрашиваются.
 */

import type { EChartsOption } from 'echarts';

/** Шрифт графиков — Manrope (как в дизайн-системе КОММА). */
export const chartFont =
  "'Manrope', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";

/** Моноширинный шрифт для числовых подписей. */
export const chartMonoFont = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

/** Фиксированная палитра КОММА для графиков (светлая тема, DESIGN_PORT_SPEC §4). */
// Палитра графиков под синюю тему приложения (--primary в global.css).
export const kommaPalette = {
  primary: '#2563EB',
  primaryDark: '#1D4ED8',
  primary100: '#DBEAFE',
  success: '#16A34A',
  warning: '#D97706',
  danger: '#DC2626',
  info: '#2563EB',
  violet: '#7C3AED',
  teal: '#0D9488',
  pink: '#DB2777',
  text: '#111827',
  textMuted: '#6B7280',
  textFaint: '#9CA3AF',
  grid: '#E5E7EB',
  surface: '#FFFFFF',
} as const;

export interface ChartTokens {
  text: string;
  textMuted: string;
  textFaint: string;
  divider: string;
  paper: string;
  primary: string;
  primaryDark: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  violet: string;
}

/**
 * Токены графиков (палитра КОММА). Аргумент сохранён для обратной совместимости
 * с непортированными потребителями (`chartTokens(theme)`), но игнорируется —
 * цвета теперь фиксированные из палитры КОММА.
 */
export function chartTokens(_theme?: unknown): ChartTokens {
  return {
    text: kommaPalette.text,
    textMuted: kommaPalette.textMuted,
    textFaint: kommaPalette.textFaint,
    divider: kommaPalette.grid,
    paper: kommaPalette.surface,
    primary: kommaPalette.primary,
    primaryDark: kommaPalette.primaryDark,
    success: kommaPalette.success,
    warning: kommaPalette.warning,
    error: kommaPalette.danger,
    info: kommaPalette.info,
    violet: kommaPalette.violet,
  };
}

/**
 * Базовый skeleton options с консистентным tooltip / textStyle / grid.
 * Каждый график расширяет через `{ ...baseChart(t), series: [...], ... }`.
 */
export function baseChart(t: ChartTokens): Partial<EChartsOption> {
  return {
    textStyle: {
      fontFamily: chartFont,
      fontSize: 12,
      color: t.text,
    },
    grid: {
      left: 48,
      right: 24,
      top: 32,
      bottom: 40,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: t.paper,
      borderColor: t.divider,
      borderWidth: 1,
      padding: 12,
      textStyle: {
        color: t.text,
        fontFamily: chartFont,
        fontSize: 12,
      },
      extraCssText: 'border-radius:10px; box-shadow:0 8px 20px rgba(17,24,39,0.08);',
      axisPointer: {
        type: 'line',
        lineStyle: { color: t.divider, type: 'dashed', width: 1 },
      },
    },
    legend: {
      top: 0,
      left: 0,
      textStyle: { color: t.textMuted, fontSize: 12, fontWeight: 400 },
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 16,
    },
    animation: true,
    animationDuration: 300,
    animationEasing: 'cubicOut',
  };
}

export function axisStyle(t: ChartTokens) {
  return {
    axisLine: { lineStyle: { color: t.divider } },
    axisTick: { show: false },
    axisLabel: { color: t.textMuted, fontSize: 11, fontFamily: chartFont },
    splitLine: { show: true, lineStyle: { color: t.divider, type: 'dashed' as const } },
  };
}
